import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ModalService } from 'ui-components/services/modal-service';
import Global from '../../../../store/global-selectors';

import { allocateFulfillmentPlansFromOrderDetails } from '../../../../store/inventory/inventory.actions';
import { downloadDocument, printDocument } from '../../../../store/documents/documents.actions';
import { printPackingSlip } from '../../../../store/printing/print.actions';
import { usePrintSettings } from '../../core/hooks';
import { DocumentType } from '../../../../enums/document-type';
import { PrintPackingSlipPrintWarningContainer } from '../../../../containers/print-packing-slip-print-warning-container';

export const usePackingSlip = ({ fulfillmentPlanIds, fulfillmentPlans, packingSlipTemplateId }) => {
  const dispatch = useDispatch();
  const isDisabled = false;
  const printSettings = usePrintSettings(DocumentType.PackingSlip.printSettingKey);
  const hasPrintedPackingSlip = fulfillmentPlans.some((fp) => fp?.packingSlipPrintedStatus?.isPrinted ?? false);
  const shouldShowReprintWarning = useSelector(Global.selectPrintSettingsShouldShowPackingSlipRePrintWarning);

  const onClick = useCallback(() => {
    dispatch(printPackingSlip(fulfillmentPlanIds, hasPrintedPackingSlip, false, false));
  }, [dispatch, fulfillmentPlanIds, hasPrintedPackingSlip]);

  const download = useCallback(() => {
    dispatch(
      allocateFulfillmentPlansFromOrderDetails(
        fulfillmentPlanIds,
        downloadDocument(DocumentType.PackingSlip, fulfillmentPlanIds, packingSlipTemplateId),
      ),
    );
  }, [dispatch, fulfillmentPlanIds, packingSlipTemplateId]);
  const onDownload = useCallback(() => {
    if (hasPrintedPackingSlip && shouldShowReprintWarning) {
      ModalService.launchModal({
        component: PrintPackingSlipPrintWarningContainer,
        props: {
          action: printPackingSlip(fulfillmentPlanIds, true, true, true),
          onYesClick: download,
        },
      });
      return;
    }

    download();
  }, [download, fulfillmentPlanIds, hasPrintedPackingSlip, shouldShowReprintWarning]);

  const print = useCallback(() => {
    dispatch(
      allocateFulfillmentPlansFromOrderDetails(
        fulfillmentPlanIds,
        printDocument(DocumentType.PackingSlip, fulfillmentPlanIds, printSettings, packingSlipTemplateId),
      ),
    );
  }, [dispatch, fulfillmentPlanIds, packingSlipTemplateId, printSettings]);
  const onPrint = useCallback(() => {
    if (hasPrintedPackingSlip && shouldShowReprintWarning) {
      ModalService.launchModal({
        component: PrintPackingSlipPrintWarningContainer,
        props: {
          action: printPackingSlip(fulfillmentPlanIds, true, true, true),
          onYesClick: print,
        },
      });
      return;
    }

    print();
  }, [fulfillmentPlanIds, hasPrintedPackingSlip, print, shouldShowReprintWarning]);

  return useMemo(
    () => ({
      isDisabled,
      onClick,
      onDownload,
      onPrint,
    }),
    [isDisabled, onClick, onDownload, onPrint],
  );
};
